import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthContext } from "@/lib/useAuthContext";

/**
 * Auth gate around the authenticated app shell.
 *
 * - ``loading`` → render the brand splash (no flash of unauthenticated
 *   chrome while /me resolves).
 * - ``unauthenticated`` → redirect to /login with ``?next=`` so the
 *   user lands back where they were after signing in.
 * - ``authenticated`` → render the children (the Layout chrome).
 */

export default function ProtectedRoute({
  children,
}: {
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  const { status } = useAuthContext();
  const location = useLocation();

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-primary-50 via-white to-gray-50">
        <div
          aria-label={t("auth.protectedRoute.loadingAsoloopAriaLabel")}
          role="status"
          className="flex items-center gap-3 text-sm font-medium text-gray-500"
        >
          <span className="size-3 animate-pulse rounded-full bg-primary-500" />
          {t("auth.protectedRoute.restoringYourSession")}
        </div>
      </div>
    );
  }

  if (status === "unauthenticated") {
    const nextPath = `${location.pathname}${location.search}`;
    const next =
      nextPath && nextPath !== "/login"
        ? `?next=${encodeURIComponent(nextPath)}`
        : "";
    return <Navigate to={`/login${next}`} replace />;
  }

  return <>{children}</>;
}
